// Centralised eligibility decision-tree & premium data for the Primes régionales simulator.
// Frontend-only. No backend, no persistence.
// Bilingual: user-facing strings are { fr, nl } objects; use pick(value, lang).
// Eligibility LOGIC, THRESHOLDS, AMOUNTS, PERCENTAGES and TREE STRUCTURE are language-agnostic
// and identical for FR/NL — only the displayed language changes.

export const START = "region";

// Resolve a bilingual field. Plain strings (identical in both languages) pass through unchanged.
export const pick = (value, lang) =>
  value && typeof value === "object" && ("fr" in value || "nl" in value) ? value[lang] : value;

// Shared Flandre conditions (shown only for eligible categories 3 & 4).
const FL_CONDITIONS = [
  {
    fr: "Avoir au moins 18 ans (ou être mineur émancipé).",
    nl: "Minstens 18 jaar oud zijn (of ontvoogde minderjarige zijn).",
  },
  {
    fr: "Être propriétaire du logement ou détenir un droit réel (copropriétaire, usufruitier, nu-propriétaire).",
    nl: "Eigenaar van de woning zijn of een zakelijk recht bezitten (mede-eigenaar, vruchtgebruiker, blote eigenaar).",
  },
  {
    fr: "Le bâtiment doit être situé en Région flamande et raccordé au réseau électrique avant le 1er janvier 2006.",
    nl: "Het gebouw moet in het Vlaamse Gewest liggen en vóór 1 januari 2006 op het elektriciteitsnet aangesloten zijn.",
  },
  {
    fr: "Le vitrage installé doit être du vitrage à haut rendement avec une valeur Ug ≤ 1,0 W/m²K.",
    nl: "Het geplaatste glas moet hoogrendementsglas zijn met een Ug-waarde ≤ 1,0 W/m²K.",
  },
  {
    fr: "Les travaux doivent être réalisés par un entrepreneur enregistré à la Banque-Carrefour des Entreprises.",
    nl: "De werken moeten worden uitgevoerd door een aannemer die is ingeschreven in de Kruispuntbank van Ondernemingen.",
  },
  {
    fr: "Le montant total des factures doit être d'au moins 1 000 € HTVA par catégorie de travaux (maximum 10 500 € HTVA).",
    nl: "Het totale factuurbedrag moet minstens 1 000 € excl. btw per categorie van werken bedragen (maximum 10 500 € excl. btw).",
  },
  {
    fr: "Les factures ne doivent pas dater de plus de 2 ans au moment de la demande.",
    nl: "De facturen mogen op het moment van de aanvraag niet ouder zijn dan 2 jaar.",
  },
  {
    fr: "Le logement doit satisfaire aux exigences de ventilation : grilles de ventilation réglables ou système de ventilation mécanique. Sans ventilation conforme, la prime est refusée.",
    nl: "De woning moet voldoen aan de ventilatie-eisen: regelbare ventilatieroosters of een mechanisch ventilatiesysteem. Zonder conforme ventilatie wordt de premie geweigerd.",
  },
  {
    fr: "Après l'octroi de la prime, un délai d'attente de 5 ans s'applique avant de pouvoir introduire une nouvelle demande pour la même catégorie de travaux.",
    nl: "Na de toekenning van de premie geldt een wachttijd van 5 jaar voordat u een nieuwe aanvraag voor dezelfde categorie van werken kunt indienen.",
  },
];

const AGE_QUESTION = { fr: "Quand votre bâtiment a-t-il été construit ?", nl: "Wanneer werd uw gebouw gebouwd?" };
const OPT_MORE_10 = { fr: "Il y a plus de 10 ans", nl: "Meer dan 10 jaar geleden" };
const OPT_LESS_10 = { fr: "Il y a moins de 10 ans", nl: "Minder dan 10 jaar geleden" };

const TVA_21 = { label: { fr: "TVA applicable", nl: "Toepasselijke btw" }, value: "21 %" };
const TVA_6 = { label: { fr: "TVA rénovation", nl: "Btw renovatie" }, value: "6 %" };

const NOT_ELIGIBLE_ANY = {
  fr: "Malheureusement, vous n'êtes éligible à aucune prime selon les critères de ce simulateur.",
  nl: "Helaas komt u volgens de criteria van deze simulator niet in aanmerking voor een premie.",
};
const NOT_ELIGIBLE_MVP = {
  fr: "Vous n'êtes pas éligible à la Mijn VerbouwPremie pour le remplacement des fenêtres et portes selon les critères de ce simulateur.",
  nl: "U komt volgens de criteria van deze simulator niet in aanmerking voor de Mijn VerbouwPremie voor de vervanging van ramen en deuren.",
};
const ELIGIBLE_MVP = {
  fr: "Vous êtes éligible à la Mijn VerbouwPremie pour le remplacement de vos fenêtres et portes, sous réserve de toutes les conditions applicables.",
  nl: "U komt in aanmerking voor de Mijn VerbouwPremie voor de vervanging van uw ramen en deuren, onder voorbehoud van alle toepasselijke voorwaarden.",
};
const ELIGIBLE_WAL = {
  fr: "Vous êtes éligible à la Prime Habitation de la Région wallonne pour le remplacement de vos châssis.",
  nl: "U komt in aanmerking voor de Prime Habitation van het Waals Gewest voor de vervanging van uw ramen.",
};
const PRIME_WAL = { fr: "Prime Habitation de la Région wallonne", nl: "Prime Habitation van het Waals Gewest" };
const FACTURE_HTVA = { fr: "min. 1 000 € — max. 10 500 €", nl: "min. 1 000 € — max. 10 500 €" };
const LBL_MVP_PCT = { fr: "Facture HTVA prise en compte", nl: "In aanmerking genomen factuur excl. btw" };
const LBL_MVP_MAX = { fr: "Montant maximum de la prime", nl: "Maximaal premiebedrag" };
const LBL_MVP_FACT = { fr: "Facture HTVA éligible", nl: "In aanmerking komende factuur excl. btw" };
const LBL_WAL_AMOUNT = { fr: "Montant", nl: "Bedrag" };
const LBL_WAL_REFUND = { fr: "Remboursement maximum", nl: "Maximale terugbetaling" };
const NOTE_M2 = { fr: "de châssis remplacé", nl: "vervangen raamoppervlak" };
const NOTE_REFUND = { fr: "du coût des travaux éligibles", nl: "van de kost van de in aanmerking komende werken" };

export const TREE = {
  // ---- STEP 1 : REGION ----
  region: {
    type: "question",
    question: { fr: "Dans quelle région se situe le bien concerné ?", nl: "In welk gewest bevindt het betrokken pand zich?" },
    options: [
      { label: { fr: "Région de Bruxelles-Capitale", nl: "Brussels Hoofdstedelijk Gewest" }, to: "bxl_age" },
      { label: { fr: "Flandre", nl: "Vlaanderen" }, to: "fl_age" },
      { label: { fr: "Wallonie", nl: "Wallonië" }, to: "wal_age" },
    ],
  },

  // ==================== BRUXELLES ====================
  bxl_age: {
    type: "question",
    question: AGE_QUESTION,
    options: [
      { label: OPT_MORE_10, to: "bxl_old" },
      { label: OPT_LESS_10, to: "bxl_recent" },
    ],
  },
  bxl_recent: { type: "result", eligible: false, intro: NOT_ELIGIBLE_ANY, tva: TVA_21 },
  bxl_old: {
    type: "result",
    eligible: false,
    tone: "info",
    prime: "RENOLUTION",
    intro: {
      fr: "En Région de Bruxelles-Capitale, des primes rénovation existent normalement via le système régional RENOLUTION. Toutefois, les primes rénovation bruxelloises concernées sont actuellement suspendues.",
      nl: "In het Brussels Hoofdstedelijk Gewest bestaan er normaal renovatiepremies via het regionale RENOLUTION-systeem. De betrokken Brusselse renovatiepremies zijn momenteel echter opgeschort.",
    },
    tva: TVA_6,
  },

  // ==================== FLANDRE ====================
  fl_age: {
    type: "question",
    question: AGE_QUESTION,
    options: [
      { label: OPT_MORE_10, to: "fl_grid" },
      { label: OPT_LESS_10, to: "fl_recent" },
    ],
  },
  fl_recent: { type: "result", eligible: false, intro: NOT_ELIGIBLE_ANY, tva: TVA_21 },
  fl_grid: {
    type: "question",
    question: {
      fr: "Le bâtiment a-t-il été raccordé au réseau électrique avant le 1er janvier 2006 ?",
      nl: "Werd het gebouw vóór 1 januari 2006 op het elektriciteitsnet aangesloten?",
    },
    options: [
      { label: { fr: "Avant le 1er janvier 2006", nl: "Vóór 1 januari 2006" }, to: "fl_income" },
      { label: { fr: "Après le 1er janvier 2006", nl: "Na 1 januari 2006" }, to: "fl_after2006" },
    ],
  },
  fl_after2006: { type: "result", eligible: false, intro: NOT_ELIGIBLE_MVP, tva: TVA_6 },
  fl_income: {
    type: "income_fl",
    question: { fr: "Quelle est votre catégorie de revenus ?", nl: "Wat is uw inkomenscategorie?" },
    help: {
      fr: "Sélectionnez d'abord votre situation, puis la tranche de revenu annuel correspondante.",
      nl: "Selecteer eerst uw situatie en vervolgens de overeenkomstige jaarinkomensschijf.",
    },
    dependentNote: {
      fr: "Par personne à charge supplémentaire, ajoutez + 4 420 € aux plafonds ci-dessus.",
      nl: "Per bijkomende persoon ten laste voegt u + 4 420 € toe aan de bovenstaande plafonds.",
    },
    situations: [
      {
        key: "single",
        label: { fr: "Célibataire", nl: "Alleenstaande" },
        rows: [
          { cat: { fr: "Catégorie 4", nl: "Categorie 4" }, threshold: { fr: "Revenu annuel ≤ 24 750 €", nl: "Jaarinkomen ≤ 24 750 €" }, to: "fl_cat4" },
          { cat: { fr: "Catégorie 3", nl: "Categorie 3" }, threshold: { fr: "Revenu annuel ≤ 43 240 €", nl: "Jaarinkomen ≤ 43 240 €" }, to: "fl_cat3" },
          { cat: { fr: "Catégorie 2", nl: "Categorie 2" }, threshold: { fr: "Revenu annuel ≤ 55 020 €", nl: "Jaarinkomen ≤ 55 020 €" }, to: "fl_cat2" },
          { cat: { fr: "Catégorie 1", nl: "Categorie 1" }, threshold: { fr: "Revenu annuel > 55 020 €", nl: "Jaarinkomen > 55 020 €" }, to: "fl_cat1" },
        ],
      },
      {
        key: "couple",
        label: { fr: "Couple ou célibataire avec 1 personne à charge", nl: "Koppel of alleenstaande met 1 persoon ten laste" },
        rows: [
          { cat: { fr: "Catégorie 4", nl: "Categorie 4" }, threshold: { fr: "Revenu annuel ≤ 37 110 €", nl: "Jaarinkomen ≤ 37 110 €" }, to: "fl_cat4" },
          { cat: { fr: "Catégorie 3", nl: "Categorie 3" }, threshold: { fr: "Revenu annuel ≤ 60 520 €", nl: "Jaarinkomen ≤ 60 520 €" }, to: "fl_cat3" },
          { cat: { fr: "Catégorie 2", nl: "Categorie 2" }, threshold: { fr: "Revenu annuel ≤ 78 610 €", nl: "Jaarinkomen ≤ 78 610 €" }, to: "fl_cat2" },
          { cat: { fr: "Catégorie 1", nl: "Categorie 1" }, threshold: { fr: "Revenu annuel > 78 610 €", nl: "Jaarinkomen > 78 610 €" }, to: "fl_cat1" },
        ],
      },
    ],
  },
  fl_cat4: {
    type: "result",
    eligible: true,
    prime: "Mijn VerbouwPremie",
    category: { fr: "Catégorie 4", nl: "Categorie 4" },
    intro: ELIGIBLE_MVP,
    metrics: [
      { label: LBL_MVP_PCT, value: "50 %" },
      { label: LBL_MVP_MAX, value: "5 250 €" },
      { label: LBL_MVP_FACT, value: FACTURE_HTVA },
    ],
    tva: TVA_6,
    conditions: FL_CONDITIONS,
  },
  fl_cat3: {
    type: "result",
    eligible: true,
    prime: "Mijn VerbouwPremie",
    category: { fr: "Catégorie 3", nl: "Categorie 3" },
    intro: ELIGIBLE_MVP,
    metrics: [
      { label: LBL_MVP_PCT, value: "35 %" },
      { label: LBL_MVP_MAX, value: "3 675 €" },
      { label: LBL_MVP_FACT, value: FACTURE_HTVA },
    ],
    tva: TVA_6,
    conditions: FL_CONDITIONS,
  },
  fl_cat2: {
    type: "result",
    eligible: false,
    category: { fr: "Catégorie 2", nl: "Categorie 2" },
    intro: NOT_ELIGIBLE_MVP,
    tva: TVA_6,
  },
  fl_cat1: {
    type: "result",
    eligible: false,
    category: { fr: "Catégorie 1", nl: "Categorie 1" },
    intro: NOT_ELIGIBLE_MVP,
    tva: TVA_6,
  },

  // ==================== WALLONIE ====================
  wal_age: {
    type: "question",
    question: AGE_QUESTION,
    options: [
      { label: OPT_MORE_10, to: "wal_15" },
      { label: OPT_LESS_10, to: "wal_recent" },
    ],
  },
  wal_recent: { type: "result", eligible: false, intro: NOT_ELIGIBLE_ANY, tva: TVA_21 },
  wal_15: {
    type: "question",
    question: { fr: "Votre bâtiment a-t-il plus de 15 ans ?", nl: "Is uw gebouw ouder dan 15 jaar?" },
    options: [
      { label: { fr: "Plus de 15 ans", nl: "Meer dan 15 jaar" }, to: "wal_income" },
      { label: { fr: "Moins de 15 ans", nl: "Minder dan 15 jaar" }, to: "wal_10_15" },
    ],
  },
  wal_10_15: {
    type: "result",
    eligible: false,
    intro: {
      fr: "Vous n'êtes pas éligible à la Prime Habitation selon les critères de ce simulateur.",
      nl: "U komt volgens de criteria van deze simulator niet in aanmerking voor de Prime Habitation.",
    },
    tva: TVA_6,
  },
  wal_income: {
    type: "income_wal",
    question: { fr: "Quelle est votre catégorie de revenus ?", nl: "Wat is uw inkomenscategorie?" },
    help: {
      fr: "Choisissez la tranche correspondant au revenu de votre ménage.",
      nl: "Kies de schijf die overeenkomt met het inkomen van uw huishouden.",
    },
    rows: [
      { cat: "R1", range: { fr: "Revenu du ménage en dessous de 26 900 €", nl: "Huishoudinkomen onder 26 900 €" }, to: "wal_r1" },
      { cat: "R2", range: { fr: "Revenu du ménage entre 26 900,01 € et 38 300 €", nl: "Huishoudinkomen tussen 26 900,01 € en 38 300 €" }, to: "wal_r2" },
      { cat: "R3", range: { fr: "Revenu du ménage entre 38 300,01 € et 50 600 €", nl: "Huishoudinkomen tussen 38 300,01 € en 50 600 €" }, to: "wal_r3" },
      { cat: "R4", range: { fr: "Revenu du ménage entre 50 600,01 € et 114 400 €", nl: "Huishoudinkomen tussen 50 600,01 € en 114 400 €" }, to: "wal_r4" },
      { cat: "R5", range: { fr: "Revenu du ménage au-dessus de 114 400 €", nl: "Huishoudinkomen boven 114 400 €" }, to: "wal_r5" },
    ],
  },
  wal_r1: {
    type: "result",
    eligible: true,
    prime: PRIME_WAL,
    category: "R1",
    intro: ELIGIBLE_WAL,
    metrics: [
      { label: LBL_WAL_AMOUNT, value: "156 €/m²", note: NOTE_M2 },
      { label: LBL_WAL_REFUND, value: "70 %", note: NOTE_REFUND },
    ],
    tva: TVA_6,
  },
  wal_r2: {
    type: "result",
    eligible: true,
    prime: PRIME_WAL,
    category: "R2",
    intro: ELIGIBLE_WAL,
    metrics: [
      { label: LBL_WAL_AMOUNT, value: "104 €/m²", note: NOTE_M2 },
      { label: LBL_WAL_REFUND, value: "70 %", note: NOTE_REFUND },
    ],
    tva: TVA_6,
  },
  wal_r3: {
    type: "result",
    eligible: true,
    prime: PRIME_WAL,
    category: "R3",
    intro: ELIGIBLE_WAL,
    metrics: [
      { label: LBL_WAL_AMOUNT, value: "78 €/m²", note: NOTE_M2 },
      { label: LBL_WAL_REFUND, value: "50 %", note: NOTE_REFUND },
    ],
    tva: TVA_6,
  },
  wal_r4: {
    type: "result",
    eligible: true,
    prime: PRIME_WAL,
    category: "R4",
    intro: ELIGIBLE_WAL,
    metrics: [
      { label: LBL_WAL_AMOUNT, value: "52 €/m²", note: NOTE_M2 },
      { label: LBL_WAL_REFUND, value: "50 %", note: NOTE_REFUND },
    ],
    tva: TVA_6,
  },
  wal_r5: {
    type: "result",
    eligible: false,
    category: "R5",
    intro: {
      fr: "Vous n'êtes pas éligible à la Prime Habitation de la Région wallonne selon les critères de ce simulateur.",
      nl: "U komt volgens de criteria van deze simulator niet in aanmerking voor de Prime Habitation van het Waals Gewest.",
    },
    tva: TVA_6,
  },
};

// Static UI strings used by the simulator shell (French + Dutch).
export const PRIMES_UI = {
  overline: { fr: "Primes & aides", nl: "Premies & steun" },
  title: { fr: "Estimez vos primes régionales.", nl: "Bereken uw regionale premies." },
  mobileTitle: { fr: "Vérifiez vos primes régionales", nl: "Controleer uw regionale premies" },
  lead: {
    fr: "Répondez à quelques questions pour découvrir, en quelques secondes, les primes potentiellement applicables au remplacement de vos châssis et portes dans votre région.",
    nl: "Beantwoord enkele vragen om in enkele seconden te ontdekken welke premies mogelijk van toepassing zijn op de vervanging van uw ramen en deuren in uw regio.",
  },
  leadNote: {
    fr: "Simulation indicative — aucune donnée n'est enregistrée.",
    nl: "Indicatieve simulatie — er worden geen gegevens opgeslagen.",
  },
  simulatorLabel: { fr: "Simulateur d'éligibilité", nl: "Geschiktheidssimulator" },
  back: { fr: "Retour", nl: "Terug" },
  restartShort: { fr: "Recommencer", nl: "Opnieuw" },
  result: { fr: "Votre résultat", nl: "Uw resultaat" },
  prime: { fr: "Prime", nl: "Premie" },
  eligible: { fr: "Éligible", nl: "In aanmerking" },
  notEligible: { fr: "Non éligible", nl: "Niet in aanmerking" },
  conditionsTitle: {
    fr: "Conditions pour obtenir la prime châssis en Flandre",
    nl: "Voorwaarden om de raampremie in Vlaanderen te verkrijgen",
  },
  conditionsIntro: {
    fr: "Pour prétendre à la Mijn VerbouwPremie pour le remplacement de fenêtres et portes, vous devez remplir toutes les conditions suivantes :",
    nl: "Om aanspraak te maken op de Mijn VerbouwPremie voor de vervanging van ramen en deuren, moet u aan alle volgende voorwaarden voldoen:",
  },
  modify: { fr: "Modifier mes réponses", nl: "Mijn antwoorden wijzigen" },
  restart: { fr: "Recommencer la simulation", nl: "Simulatie opnieuw starten" },
  rdvCta: { fr: "Prendre rendez-vous", nl: "Afspraak maken" },
};
